import { Card, CardContent } from "@/components/ui/card";
import {
  Award,
  CheckCircle,
  Eye,
  Heart,
  Lightbulb,
  Linkedin,
  Mail,
  Shield,
  Target,
  Users,
} from "lucide-react";
import React from "react";
import Footer from "../Footer";
import Header from "../Header";
import MiniFooter from "../MiniFooter";

const missions = [
  {
    icon: Target,
    title: "Notre Mission",
    description:
      "Démocratiser l'accès à l'information juridique en combinant l'intelligence artificielle et l'expertise humaine pour offrir des réponses fiables et instantanées aux professionnels du droit.",
  },
  {
    icon: Eye,
    title: "Notre Vision",
    description:
      "Devenir la référence mondiale en matière de recherche juridique intelligente, où chaque professionnel peut accéder à l'information dont il a besoin, quand il en a besoin.",
  },
  {
    icon: Heart,
    title: "Nos Engagements",
    description:
      "Garantir la qualité, la fiabilité et l'éthique dans chaque réponse fournie, tout en respectant la confidentialité et la sécurité des données de nos utilisateurs.",
  },
];

const values = [
  {
    icon: Shield,
    title: "Fiabilité",
    description:
      "Chaque information est vérifiée et certifiée par nos experts juridiques",
  },
  {
    icon: Users,
    title: "Collaboration",
    description:
      "Nous croyons en la force du travail d'équipe entre IA et expertise humaine",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description:
      "Nous repoussons constamment les limites de la technologie juridique",
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Nous visons l'excellence dans chaque aspect de notre service",
  },
];

const teamMembers = [
  {
    name: "Dr. Marie Dubois",
    role: "CEO & Co-fondatrice",
    description:
      "Docteure en droit, 15 ans d'expérience en droit des affaires. Ancienne associée chez un grand cabinet parisien.",
    image: "/placeholder.svg?height=300&width=300",
    linkedin: "#",
    email: "#",
  },
  {
    name: "Thomas Martin",
    role: "CTO & Co-fondateur",
    description:
      "Ingénieur en IA, expert en traitement du langage naturel. Ancien lead developer chez une fintech européenne.",
    image: "/placeholder.svg?height=300&width=300",
    linkedin: "#",
    email: "#",
  },
  {
    name: "Sophie Laurent",
    role: "Directrice Juridique",
    description:
      "Juriste spécialisée en droit numérique, responsable de la certification et de la qualité des contenus.",
    image: "/placeholder.svg?height=300&width=300",
    linkedin: "#",
    email: "#",
  },
];

const timelineEvents = [
  {
    year: "2025",
    title: "Création de Docuveritas",
    description:
      "Fondation de l'entreprise par Marie Dubois et Thomas Martin avec une vision claire : révolutionner l'accès à l'information juridique.",
  },
];

const partnerships = [
  {
    category: "Institutions Juridiques",
    partners: [
      "Barreau de Paris",
      "Conseil National des Barreaux",
      "École de Formation du Barreau",
    ],
  },
  {
    category: "Universités & Recherche",
    partners: [
      "Université Paris 1 Panthéon-Sorbonne",
      "Sciences Po Paris",
      "Institut de Recherche Juridique",
    ],
  },
  {
    category: "Associations Professionnelles",
    partners: [
      "Association des Juristes d'Entreprise",
      "Union Internationale des Avocats",
      "Cercle Montesquieu",
    ],
  },
  {
    category: "Certifications & Labels",
    partners: ["ISO 27001", "RGPD Compliant", "Label France Cybersécurité"],
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <section className="bg-gradient-to-br from-blue-50 to-white py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Qui sommes-nous ?
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                Nous sommes une équipe passionnée de juristes et d'experts
                technologiques, unis par une mission commune :{" "}
                <span className="text-blue-600 font-semibold">
                  révolutionner l'accès à l'information juridique
                </span>
                .
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Depuis notre création, nous développons des solutions innovantes
                qui permettent aux professionnels du droit de gagner en
                efficacité tout en garantissant la fiabilité et la précision de
                l'information juridique.
              </p>
            </div>
          </div>
        </section>
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Notre raison d'être
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Découvrez les valeurs et principes qui guident notre action
                quotidienne
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {missions.map((mission, index) => (
                <Card
                  key={index}
                  className="border-2 hover:border-slate-300 transition-all duration-300 rounded-xl h-full"
                >
                  <CardContent className="p-8 text-center h-full flex flex-col">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <mission.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {mission.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed flex-grow">
                      {mission.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos valeurs fondamentales
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Les principes qui définissent notre culture d'entreprise et
                notre approche du service
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 bg-blue-100 rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6 group-hover:shadow-xl transition-shadow duration-300">
                    <value.icon className="h-10 w-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Notre équipe
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Rencontrez les experts passionnés qui donnent vie à Docuveritas
                chaque jour
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <Card
                  key={index}
                  className="border-2 hover:border-slate-300 transition-all duration-300 rounded-xl overflow-hidden group"
                >
                  <CardContent className="p-0">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={member.image || "/placeholder.svg"}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {member.name}
                      </h3>
                      <p className="text-slate-500 font-semibold mb-3">
                        {member.role}
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">
                        {member.description}
                      </p>
                      <div className="flex space-x-3">
                        <a
                          href={member.linkedin}
                          className="text-slate-400 hover:text-slate-500 transition-colors"
                          aria-label={`LinkedIn de ${member.name}`}
                        >
                          <Linkedin className="h-5 w-5" />
                        </a>
                        <a
                          href={member.email}
                          className="text-slate-400 hover:text-slate-500 transition-colors"
                          aria-label={`Email de ${member.name}`}
                        >
                          <Mail className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Notre histoire
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Découvrez les étapes clés qui ont marqué l'évolution de
                Docuveritas
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 transform md:-translate-x-1/2"></div>

              <div className="space-y-12">
                {timelineEvents.map((event, index) => (
                  <div
                    key={index}
                    className={`relative flex items-center ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-slate-500 rounded-full transform md:-translate-x-1/2 z-10"></div>

                    {/* Content */}
                    <div
                      className={`ml-12 md:ml-0 md:w-1/2 ${
                        index % 2 === 0 ? "md:pr-12" : "md:pl-12"
                      }`}
                    >
                      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                        <div className="flex items-center mb-3">
                          <span className="text-2xl font-bold text-slate-500 mr-3">
                            {event.year}
                          </span>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                          {event.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos partenaires et certifications
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Nous collaborons avec les institutions les plus prestigieuses du
                secteur juridique
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {partnerships.map((partnership, index) => (
                <Card
                  key={index}
                  className="border-2 hover:border-slate-300 transition-colors rounded-xl"
                >
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                      {partnership.category}
                    </h3>
                    <div className="space-y-3">
                      {partnership.partners.map((partner, partnerIndex) => (
                        <div
                          key={partnerIndex}
                          className="flex items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-gray-700 font-medium text-center">
                            {partner}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        <MiniFooter />
      </main>
      <Footer />
    </div>
  );
}
